import type { SelectVendorItem } from "@au-schein-de/au-ui-library";
import type { ProductVendorListItem } from "@dransay/api/models";
import type { OfferCardDeliveryBadge } from "~/features/products/product-details-vendor-select-trigger.component";
import { formatCentsAsAmount } from "~/hooks/use-currency-price.hook";

export interface ProductDetailsSelectVendorItem
	extends Omit<SelectVendorItem, "price"> {
	price: number;
	combinedPaymentEnabled?: boolean;
	addressData?: ProductVendorListItem["address"];
	distanceMeters?: number | null;
	deliveryMethodsData?: ProductVendorListItem["deliveryMethods"];
	disabled?: boolean;
}

type VendorDeliveryMethod = NonNullable<
	ProductVendorListItem["deliveryMethods"]
>[number];

export const createOfferCardDeliveryBadges = ({
	deliveryMethodsData,
	locale,
	formatFreeFromLabel,
}: {
	deliveryMethodsData?: ProductVendorListItem["deliveryMethods"];
	locale: string;
	formatFreeFromLabel: (amount: string) => string;
}): OfferCardDeliveryBadge[] => {
	const enabledMethods = (deliveryMethodsData ?? []).filter((m) => m.enabled);
	const getMinFreeFromCents = (
		kind: VendorDeliveryMethod["kind"],
	): number | undefined => {
		const candidates = enabledMethods
			.filter((m) => m.kind === kind && m.cost != null && m.cost > 0)
			.map((m) => m.freeFrom)
			.filter((v): v is number => typeof v === "number");
		if (candidates.length === 0) return undefined;
		return Math.min(...candidates);
	};

	const hasKind = (kind: VendorDeliveryMethod["kind"]) =>
		enabledMethods.some((m) => m.kind === kind);

	const badges: OfferCardDeliveryBadge[] = [];

	if (hasKind("shipping")) {
		const freeFromCents = getMinFreeFromCents("shipping");
		badges.push({
			kind: "shipping",
			label:
				freeFromCents !== undefined
					? formatFreeFromLabel(formatCentsAsAmount(freeFromCents, locale))
					: undefined,
		});
	}

	if (hasKind("courier")) {
		const freeFromCents = getMinFreeFromCents("courier");
		badges.push({
			kind: "courier",
			label:
				freeFromCents !== undefined
					? formatFreeFromLabel(formatCentsAsAmount(freeFromCents, locale))
					: undefined,
		});
	}

	if (hasKind("pickup")) {
		badges.push({ kind: "pickup" });
	}

	return badges;
};
